import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import {
  EDUCATION_LEVELS,
  EDUCATION_LEVEL_LABELS,
  ICP_ALIASES_MAX,
  ICP_CITY_MAX,
  ICP_EVIDENCE_MAX,
  ICP_FACT_TYPES,
  ICP_FACT_TYPE_LABELS,
  ICP_GROUP_LABELS,
  ICP_JEV_ITEMS_MAX,
  ICP_MUST_RATING,
  ICP_RATINGS,
  ICP_RATING_LABELS,
  ICP_SOURCE_LABELS,
  ICP_TEXT_MAX,
  ICP_YEARS_MAX,
  ICP_YEARS_STEP,
  icpFactTextOf,
  saveIcpSchema,
  type IcpFact,
  type IcpFactType,
  type IcpGroup,
  type IcpItem,
  type IcpItemRequest,
  type SaveIcpInput,
  type SaveIcpRequest,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Input, Textarea } from '../../../components/Input';
import { Popover, usePopover } from '../../../components/Popover';
import { RatingDots } from '../../../components/RatingDots';
import { Select } from '../../../components/Select';
import { TagInput } from '../../../components/TagInput';
import { itemInputOf, itemOfInput, jevLineCount, newFactItem } from '../icp-form';
import { dockedPanel } from './panel-parts';
import styles from './icp.module.css';

// ADR 0061, mockup screen 3: the editable profile in four groups. Skills and habits say what counts as evidence, English why
// it matters, facts when they count as met (the title of a fact is written by the app, never typed). Every line has 1–5 dots,
// add and remove. Unfair words are refused on the line as you type, with the same words the server uses. ADR 0076 redrew it:
// lines in calm lists rather than a box each, edited where they are read, and empty groups folded away.

const ICP_RATING_HINTS: Readonly<Record<string, string>> = Object.fromEntries(ICP_RATINGS.map((rating) => [String(rating), ICP_RATING_LABELS[rating]]));
const REMOTE_NOTE = 'This job is remote, so where they live isn’t checked.';
const FORM_GROUPS: readonly IcpGroup[] = ['skills', 'habits', 'communication', 'facts'];
const YEARS_UNIT_SUFFIX = 'years';
const DAYS_UNIT_SUFFIX = 'days';

export type BudgetNote = { readonly kind: 'amount'; readonly text: string } | { readonly kind: 'hidden' } | { readonly kind: 'none' };

interface IcpProfileFormProps {
  /** The lines the form starts from: the profile as it is, or a draft from the interview. */
  readonly defaultItems: readonly IcpItem[];
  /** What the draft left out, in plain words ("Left out a suggestion about who a person is."). */
  readonly notes: readonly string[];
  readonly isRemote: boolean;
  readonly budget: BudgetNote;
  /** "1.6¢" — the interview and draft so far; null before there was one. */
  readonly interviewCost: string | null;
  /** "Ranking 96 CVs with Jev · about 6¢" for the lines as they stand. */
  readonly estimate: (items: readonly IcpItemRequest[]) => string;
  /** ADR 0070 stage 2: Save and rank with the job's Jev budget: what is left on the button, and why it is off when the ranking would not fit. */
  readonly saveBudget?: (items: readonly IcpItemRequest[]) => { readonly leftSuffix: string; readonly blockedNote: string | null };
  /** "Ask me more questions" starts an interview, which is priced by use: off only when nothing is left of the job's Jev budget. */
  readonly moreBudget?: { readonly blockedNote: string | null };
  readonly onChange: (items: readonly IcpItem[]) => void;
  readonly onSave: (items: readonly IcpItemRequest[]) => void;
  readonly onMore: (items: readonly IcpItemRequest[]) => void;
  readonly onClose: () => void;
  readonly makeId?: () => string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A message at a path inside RHF's nested errors, whatever shape the resolver gave it. */
export function messageAt(errors: unknown, path: readonly (string | number)[]): string | undefined {
  let here: unknown = errors;
  for (const step of path) {
    if (!isRecord(here)) return undefined;
    here = here[step];
  }
  if (!isRecord(here)) return undefined;
  const message = here['message'];
  return typeof message === 'string' ? message : undefined;
}

function blankLine(group: 'skills' | 'habits', id: string): IcpItemRequest {
  return { id, kind: 'jev', group, text: '', evidence: '', rating: 3, source: 'added' };
}

function blankEnglish(id: string): IcpItemRequest {
  return { id, kind: 'english', group: 'communication', text: 'Clear written English', evidence: '', rating: 3, source: 'added' };
}

interface IcpFormOptions {
  readonly defaultItems: readonly IcpItem[];
  readonly isRemote: boolean;
  readonly onChange: (items: readonly IcpItem[]) => void;
  readonly makeId?: () => string;
}

/** The form's lines and what they allow: shared by the docked form on Ranking and the inline one in job setup. */
export interface IcpFormState {
  readonly form: UseFormReturn<SaveIcpRequest, unknown, SaveIcpInput>;
  readonly items: readonly IcpItemRequest[];
  readonly jevCount: number;
  readonly isBlank: boolean;
  readonly hasRemoteLocation: boolean;
  readonly canSave: boolean;
  readonly isFull: boolean;
  readonly setItems: (next: readonly IcpItemRequest[]) => void;
  readonly change: (id: string, patch: (item: IcpItemRequest) => IcpItemRequest) => void;
  readonly makeId: () => string;
}

export function useIcpForm({ defaultItems, isRemote, onChange, makeId: makeIdOption }: IcpFormOptions): IcpFormState {
  const makeId = makeIdOption ?? ((): string => crypto.randomUUID());
  const form = useForm<SaveIcpRequest, unknown, SaveIcpInput>({
    resolver: zodResolver(saveIcpSchema),
    mode: 'onChange',
    defaultValues: { version: null, items: defaultItems.map(itemInputOf), interviewId: null, rank: true },
  });
  const items = useWatch({ control: form.control, name: 'items' });

  function setItems(next: readonly IcpItemRequest[]): void {
    form.setValue('items', [...next], { shouldValidate: true, shouldDirty: true });
    onChange(next.map(itemOfInput));
  }

  function change(id: string, patch: (item: IcpItemRequest) => IcpItemRequest): void {
    setItems(items.map((item) => (item.id === id ? patch(item) : item)));
  }

  const hasRemoteLocation = isRemote && items.some((item) => item.kind === 'fact' && item.fact.type === 'location');
  const isBlank = items.length === 0;
  const jevCount = jevLineCount(items);
  return {
    form,
    items,
    jevCount,
    isBlank,
    hasRemoteLocation,
    canSave: form.formState.isValid && !isBlank && !hasRemoteLocation,
    isFull: jevCount >= ICP_JEV_ITEMS_MAX,
    setItems,
    change,
    makeId,
  };
}

interface IcpFormGroupsProps {
  readonly state: IcpFormState;
  readonly notes: readonly string[];
  readonly isRemote: boolean;
  readonly budget: BudgetNote;
}

/** Which group each "add" button fills, and what it says. */
const ADD_LABELS: Readonly<Record<IcpGroup, string>> = {
  skills: 'Add a skill',
  habits: 'Add a work habit',
  communication: 'Add English',
  facts: 'Add a fact',
};

/**
 * The lines a recruiter edits, wherever the form is shown (ADR 0061; redrawn by ADR 0076 to be calmer to read).
 * Each group is one list with hairlines between its lines, never a box per line. A line reads as text and is
 * edited where it is read; its rating sits on the right with what the number means; a must-have is marked down
 * its left edge. Groups with nothing in them fold into one "Also look for" row at the end, so a short profile is
 * a short page. Skills always shows: it is where nearly every profile starts.
 */
export function IcpFormGroups({ state, notes, isRemote, budget }: IcpFormGroupsProps): ReactNode {
  const { form, items, isFull, setItems, change, makeId } = state;
  /** A line's title shows its error once it has been left, or once something is typed — never on a line just added. */
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const errors = form.formState.errors;
  const usedFacts = useMemo(() => new Set(items.flatMap((item) => (item.kind === 'fact' ? [item.fact.type] : []))), [items]);
  const missingFacts = ICP_FACT_TYPES.filter((type) => !usedFacts.has(type) && !(isRemote && type === 'location'));
  const hasEnglish = items.some((item) => item.kind === 'english');
  const shown = FORM_GROUPS.filter((group) => group === 'skills' || items.some((item) => item.group === group));
  const folded = FORM_GROUPS.filter((group) => !shown.includes(group) && canAdd(group));

  function canAdd(group: IcpGroup): boolean {
    if (group === 'communication') return !hasEnglish;
    if (group === 'facts') return missingFacts.length > 0;
    return true;
  }

  function add(group: Exclude<IcpGroup, 'facts'>): void {
    setItems([...items, group === 'communication' ? blankEnglish(makeId()) : blankLine(group, makeId())]);
  }

  function addFactOfType(type: IcpFactType): void {
    setItems([...items, itemInputOf(newFactItem(type, makeId()))]);
  }

  const addButton = (group: IcpGroup, isQuiet: boolean): ReactNode => {
    if (!canAdd(group)) return null;
    const isJevFull = (group === 'skills' || group === 'habits') && isFull;
    if (group === 'facts') return <AddFact key={group} missing={missingFacts} isQuiet={isQuiet} onAdd={addFactOfType} />;
    return (
      <button
        key={group}
        type="button"
        className={isQuiet ? styles.addChip : styles.addLine}
        disabled={isJevFull}
        title={isJevFull ? `Jev reads up to ${ICP_JEV_ITEMS_MAX} lines.` : undefined}
        onClick={() => add(group)}
      >
        <Plus size={14} aria-hidden />
        {ADD_LABELS[group]}
      </button>
    );
  };

  return (
    <div className={styles.lines}>
      {notes.length > 0 && (
        <ul className={styles.notes} aria-label="What the draft left out">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
      {shown.map((group) => {
        const lines = items.filter((item) => item.group === group);
        return (
          <section key={group} className={styles.fgroup} aria-label={ICP_GROUP_LABELS[group]}>
            <div className={styles.fgroupHead}>
              <h3 className={styles.fgroupTitle}>{ICP_GROUP_LABELS[group]}</h3>
              {lines.length > 0 && (
                <span className={styles.fgroupCount} aria-label={lines.length === 1 ? '1 line' : `${lines.length} lines`}>
                  {lines.length}
                </span>
              )}
            </div>
            <div className={styles.flist}>
              {lines.length === 0 && <p className={styles.flistEmpty}>Nothing yet. What must a good CV show?</p>}
              {lines.map((item) => {
                const index = items.indexOf(item);
                return (
                  <FormLine
                    key={item.id}
                    item={item}
                    index={index}
                    errors={errors}
                    isTouched={touched.has(item.id)}
                    isRemote={isRemote}
                    budget={budget}
                    onChange={(patch) => change(item.id, patch)}
                    onTouch={() => setTouched((current) => new Set(current).add(item.id))}
                    onRemove={() => setItems(items.filter((entry) => entry.id !== item.id))}
                  />
                );
              })}
              {addButton(group, false)}
            </div>
          </section>
        );
      })}
      {folded.length > 0 && (
        <div className={styles.alsoRow}>
          <span className={styles.alsoLabel}>Also look for</span>
          {folded.map((group) => addButton(group, true))}
        </div>
      )}
    </div>
  );
}

/** "Add a fact": a menu of the facts not used yet, anchored to its button. */
function AddFact({ missing, isQuiet, onAdd }: { readonly missing: readonly IcpFactType[]; readonly isQuiet: boolean; readonly onAdd: (type: IcpFactType) => void }): ReactNode {
  const { triggerRef, anchor, isOpen, toggle, close } = usePopover();
  return (
    <>
      <button ref={triggerRef} type="button" className={isQuiet ? styles.addChip : styles.addLine} aria-haspopup="menu" aria-expanded={isOpen} onClick={toggle}>
        <Plus size={14} aria-hidden />
        {ADD_LABELS.facts}
      </button>
      <Popover anchor={anchor} onClose={close} label={ADD_LABELS.facts}>
        <div className={styles.menu} role="menu">
          {missing.map((type) => (
            <button
              key={type}
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={() => {
                close();
                onAdd(type);
              }}
            >
              {ICP_FACT_TYPE_LABELS[type]}
            </button>
          ))}
        </div>
      </Popover>
    </>
  );
}

export function IcpProfileForm(props: IcpProfileFormProps): ReactNode {
  const { defaultItems, notes, isRemote, budget, interviewCost, estimate, saveBudget, moreBudget, onChange, onSave, onMore, onClose } = props;
  const state = useIcpForm({ defaultItems, isRemote, onChange, ...(props.makeId ? { makeId: props.makeId } : {}) });
  const { items, jevCount, isBlank, hasRemoteLocation, canSave } = state;
  const [estimateLead, estimateCost] = splitEstimate(estimate(items));
  const save = saveBudget?.(items);

  const footer = (
    <>
      <div className={styles.cost}>
        <span>
          {jevCount} of {ICP_JEV_ITEMS_MAX} lines Jev reads · facts are free
        </span>
        {interviewCost !== null && (
          <span>
            Interview and draft: <b>{interviewCost}</b>
          </span>
        )}
      </div>
      <div className={styles.footRow}>
        <Button variant="solid" disabled={!canSave} isBlocked={Boolean(save?.blockedNote)} onClick={() => onSave(items)}>
          Save and rank with this{save?.leftSuffix ?? ''}
        </Button>
        <Button variant="outline" isBlocked={Boolean(moreBudget?.blockedNote)} onClick={() => onMore(items)}>
          Ask me more questions
        </Button>
      </div>
      {(save?.blockedNote ?? moreBudget?.blockedNote) && (
        <p className={styles.error} role="status">
          {save?.blockedNote ?? moreBudget?.blockedNote}
        </p>
      )}
      {!canSave && !isBlank && (
        <p className={styles.error} role="status">
          {hasRemoteLocation ? REMOTE_NOTE : 'Fix the lines marked above to save.'}
        </p>
      )}
      <div className={styles.cost}>
        <span>{estimateLead}</span>
        {estimateCost && <span>{estimateCost}</span>}
      </div>
    </>
  );

  return dockedPanel({
    title: 'Ideal candidate',
    sub: 'Drafted from the job and your answers. Change, remove or add anything.',
    onClose,
    footer,
    children: <IcpFormGroups state={state} notes={notes} isRemote={isRemote} budget={budget} />,
  });
}

function splitEstimate(label: string): readonly [string, string | null] {
  const at = label.lastIndexOf(' · ');
  return at < 0 ? [label, null] : [label.slice(0, at), label.slice(at + 3)];
}

interface FormLineProps {
  readonly item: IcpItemRequest;
  readonly index: number;
  readonly errors: unknown;
  readonly isTouched: boolean;
  readonly isRemote: boolean;
  readonly budget: BudgetNote;
  readonly onChange: (patch: (item: IcpItemRequest) => IcpItemRequest) => void;
  readonly onTouch: () => void;
  readonly onRemove: () => void;
}

/**
 * One line: what to look for and what counts as evidence on the left, read as text and edited in place; how much it
 * matters on the right, the number with its meaning beside it. "From your answer" is the one source worth a tag —
 * it ties the line to something HR said in the interview (ADR 0061 §8).
 */
function FormLine({ item, index, errors, isTouched, isRemote, budget, onChange, onTouch, onRemove }: FormLineProps): ReactNode {
  const title = item.kind === 'fact' ? icpFactTextOf(factOf(item.fact)) : item.text === '' ? 'a new line' : item.text;
  const textError = messageAt(errors, ['items', index, 'text']);
  const showTextError = textError !== undefined && (isTouched || (item.kind !== 'fact' && item.text !== ''));
  const evidenceError = messageAt(errors, ['items', index, 'evidence']);
  return (
    <div className={styles.fline} data-must={item.rating === ICP_MUST_RATING || undefined}>
      <div className={styles.flineMain}>
        {item.kind === 'fact' ? (
          <span className={styles.factTitle}>{title}</span>
        ) : (
          <div className={styles.flineTitle}>
            <Input
              variant="quiet"
              aria-label={`${ICP_GROUP_LABELS[item.group]} line ${index + 1}: title`}
              value={item.text}
              maxLength={ICP_TEXT_MAX}
              isInvalid={showTextError}
              placeholder={item.kind === 'english' ? 'English' : 'What to look for'}
              onBlur={onTouch}
              onChange={(event) => {
                const text = event.target.value;
                onChange((current) => (current.kind === 'fact' ? current : { ...current, text }));
              }}
            />
          </div>
        )}
        {showTextError && (
          <p className={styles.error} role="alert">
            {textError}
          </p>
        )}
        {item.kind === 'fact' ? (
          <FactControls item={item} index={index} errors={errors} isRemote={isRemote} budget={budget} onChange={onChange} />
        ) : (
          <div className={styles.evidenceField}>
            <Textarea
              variant="quiet"
              aria-label={item.kind === 'english' ? 'Why it matters' : 'What counts as evidence'}
              placeholder={item.kind === 'english' ? 'Why English matters in this role' : 'What in a CV would show it'}
              value={item.evidence ?? ''}
              maxLength={ICP_EVIDENCE_MAX}
              isInvalid={evidenceError !== undefined}
              rows={1}
              onChange={(event) => {
                const evidence = event.target.value;
                onChange((current) => (current.kind === 'fact' ? current : { ...current, evidence }));
              }}
            />
          </div>
        )}
        {evidenceError && (
          <p className={styles.error} role="alert">
            {evidenceError}
          </p>
        )}
        {item.source === 'answer' && <span className={styles.source}>{ICP_SOURCE_LABELS.answer}</span>}
      </div>
      <div className={styles.flineSide}>
        <span className={styles.rating}>
          <RatingDots
            size="sm"
            label={`Focus for ${title}`}
            value={item.rating}
            hints={ICP_RATING_HINTS}
            onChange={(value) => {
              const rating = ICP_RATINGS.find((entry) => entry === value);
              if (rating) onChange((current) => ({ ...current, rating }));
            }}
          />
          <span className={styles.ratingLabel} aria-hidden>
            {ICP_RATING_LABELS[item.rating]}
          </span>
        </span>
        <button type="button" className={styles.iconButton} aria-label={`Remove ${title}`} title="Remove" onClick={onRemove}>
          <Trash2 size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
}

/** A fact as the shared title-maker wants it, with the optional parts filled in. */
function factOf(fact: Extract<IcpItemRequest, { kind: 'fact' }>['fact']): IcpFact {
  if (fact.type === 'years') return { ...fact, maxYears: fact.maxYears ?? null };
  if (fact.type === 'location') return { ...fact, aliases: fact.aliases ?? [] };
  return fact;
}

interface FactControlsProps {
  readonly item: Extract<IcpItemRequest, { kind: 'fact' }>;
  readonly index: number;
  readonly errors: unknown;
  readonly isRemote: boolean;
  readonly budget: BudgetNote;
  readonly onChange: (patch: (item: IcpItemRequest) => IcpItemRequest) => void;
}

function withFact(onChange: FactControlsProps['onChange'], fact: Extract<IcpItemRequest, { kind: 'fact' }>['fact']): void {
  onChange((current) => (current.kind === 'fact' ? { ...current, fact } : current));
}

function numberOf(text: string): number {
  const value = Number(text);
  return Number.isFinite(value) ? value : 0;
}

function FactControls({ item, index, errors, isRemote, budget, onChange }: FactControlsProps): ReactNode {
  const { fact } = item;
  const factError = (path: string): string | undefined => messageAt(errors, ['items', index, 'fact', path]);
  if (fact.type === 'years') {
    const problem = factError('maxYears') ?? factError('minYears');
    return (
      <div className={styles.factRow}>
        <span className={styles.factRowLabel}>Counts as met when</span>
        <span className={styles.factInput}>
          <Input
            type="number"
            aria-label="Minimum years"
            isNumeric
            min={0}
            max={ICP_YEARS_MAX}
            step={ICP_YEARS_STEP}
            value={fact.minYears}
            isInvalid={problem !== undefined}
            onChange={(event) => withFact(onChange, { ...fact, minYears: numberOf(event.target.value) })}
          />
        </span>
        <span className={styles.factRowLabel}>to</span>
        <span className={styles.factInput}>
          <Input
            type="number"
            aria-label="Top years, or blank for no top"
            isNumeric
            min={0}
            max={ICP_YEARS_MAX}
            step={ICP_YEARS_STEP}
            placeholder="no top"
            value={fact.maxYears ?? ''}
            isInvalid={problem !== undefined}
            onChange={(event) => withFact(onChange, { ...fact, maxYears: event.target.value === '' ? null : numberOf(event.target.value) })}
          />
        </span>
        <span className={styles.factRowLabel}>{YEARS_UNIT_SUFFIX}</span>
        <span className={styles.factNote}>{fact.maxYears === null || fact.maxYears === undefined ? 'Blank means no top.' : 'Above the top counts half.'}</span>
        {problem && (
          <p className={styles.error} role="alert">
            {problem}
          </p>
        )}
      </div>
    );
  }
  if (fact.type === 'education') {
    return (
      <div className={styles.factRow}>
        <span className={styles.factRowLabel}>Counts as met when</span>
        <span className={styles.factWide}>
          <Select
            label="Lowest education"
            value={fact.minLevel}
            options={EDUCATION_LEVELS.map((level) => ({ value: level, label: EDUCATION_LEVEL_LABELS[level] }))}
            onChange={(value) => {
              const minLevel = EDUCATION_LEVELS.find((level) => level === value);
              if (minLevel) withFact(onChange, { type: 'education', minLevel });
            }}
          />
        </span>
        <span className={styles.factRowLabel}>or higher</span>
      </div>
    );
  }
  if (fact.type === 'location') {
    const cityError = factError('city');
    return (
      <div className={styles.factRow}>
        <span className={styles.factRowLabel}>Counts as met when they live in</span>
        <span className={styles.factWide}>
          <Input aria-label="City" value={fact.city} maxLength={ICP_CITY_MAX} isInvalid={cityError !== undefined} placeholder="City" onChange={(event) => withFact(onChange, { ...fact, city: event.target.value })} />
        </span>
        <span className={styles.factWide}>
          <TagInput value={fact.aliases ?? []} maxItems={ICP_ALIASES_MAX} maxLength={ICP_CITY_MAX} placeholder="Other spellings" onChange={(aliases) => withFact(onChange, { ...fact, aliases: [...aliases] })} />
        </span>
        <span className={styles.factNote}>{isRemote ? REMOTE_NOTE : 'Willing to move counts half.'}</span>
        {cityError && (
          <p className={styles.error} role="alert">
            {cityError}
          </p>
        )}
      </div>
    );
  }
  if (fact.type === 'salary') {
    return (
      <div className={styles.factRow}>
        <span className={styles.factRowLabel}>Counts as met when</span>
        <span className={styles.factTitle}>{budget.kind === 'amount' ? `At or under the job’s budget top (${budget.text})` : budget.kind === 'hidden' ? 'At or under the job’s budget top (hidden from you)' : 'Set a budget in Basics first'}</span>
      </div>
    );
  }
  return (
    <div className={styles.factRow}>
      <span className={styles.factRowLabel}>Counts as met when they can start within</span>
      <span className={styles.factInput}>
        <Input type="number" aria-label="Days to start" isNumeric min={0} step={1} value={fact.withinDays} onChange={(event) => withFact(onChange, { type: 'notice', withinDays: numberOf(event.target.value) })} />
      </span>
      <span className={styles.factRowLabel}>{DAYS_UNIT_SUFFIX}</span>
      <span className={styles.factNote}>Up to double that counts half; blank is unknown.</span>
    </div>
  );
}
